import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  Paper,
  BottomDrawer,
  ConfirmDialog,
  DetailField,
  EntityDetailShell,
  formatAddress,
} from '@vipso/shared';
import {
  useGetCustomerQuery,
  useAddUpdateCustomerMutation,
  useDeleteCustomerMutation,
} from '../api/tenantApi';
import {
  CustomerForm,
  createEmptyCustomerForm,
  customerFormInput,
  customerFormValues,
  isCustomerPhoneValid,
  type CustomerFormValues,
} from '../features/customers/CustomerForm';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';

export function CustomerDetailPage() {
  const access = useModuleAccess('customer');
  const approvalAccess = useApprovalModuleAccess();
  const { t } = useTranslation();
  const terms = useModuleTerminology('customer');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetCustomerQuery(id!, { skip: !id });
  const [addUpdateCustomer, { isLoading: saving }] = useAddUpdateCustomerMutation();
  const [deleteCustomer, { isLoading: deleting }] = useDeleteCustomerMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState<CustomerFormValues>(createEmptyCustomerForm);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const customer = data?.getCustomer;

  function openEdit() {
    if (!customer) return;
    setPhoneError(null);
    setForm(customerFormValues(customer));
    setEditOpen(true);
  }

  async function handleSave() {
    if (!customer) return;
    if (!isCustomerPhoneValid(form)) {
      setPhoneError(t('phone.invalid'));
      return;
    }
    setPhoneError(null);
    await addUpdateCustomer(customerFormInput(form, customer)).unwrap();
    setEditOpen(false);
  }

  async function handleDelete() {
    if (!customer) return;
    await deleteCustomer(customer.id).unwrap();
    setConfirmOpen(false);
    navigate('/customers');
  }

  return (
    <EntityDetailShell
      isLoading={isLoading && !customer}
      notFound={isError || !customer}
      notFoundTitle={t('terminology.notFound', { entity: terms.singular })}
      backLabel={t('terminology.backToList', { entity: terms.plural })}
      onBack={() => navigate('/customers')}
      title={customer?.name ?? ''}
      description={t('terminology.details', { entity: terms.singular })}
      actions={
        access.canUpdate || access.canDelete ? (
          <Group gap="sm">
            {access.canUpdate ? (
              <Button variant="light" onClick={openEdit}>
                {t('common.edit')}
              </Button>
            ) : null}
            {access.canDelete ? (
              <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
                {t('common.delete')}
              </Button>
            ) : null}
          </Group>
        ) : undefined
      }
    >
      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <DetailField label={t('common.phone')} value={customer?.phone} />
          <DetailField label={t('common.email')} value={customer?.email} />
          {approvalAccess.customer ? (
            <DetailField
              label={t('approval.status')}
              value={<ApprovalStatusBadge status={customer?.approvalStatus} />}
            />
          ) : null}
          <DetailField label={t('customers.openingBalance')} value={customer?.openingBalance ?? 0} />
          <DetailField label={t('common.address')} value={formatAddress(customer?.address)} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="CUSTOMER"
        subjectId={customer?.id}
        entityApprovalStatus={customer?.approvalStatus}
        enabled={approvalAccess.customer}
      />

      <Button
        onClick={() => navigate(`/orders?customerIds=${customer?.id}`)}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('common.transactions')}
      </Button>

      <BottomDrawer opened={editOpen} onClose={() => setEditOpen(false)} title={t('terminology.edit', { entity: terms.singular })}>
        <Stack gap="md">
          <CustomerForm
            value={form}
            phoneError={phoneError}
            onChange={(next) => {
              setPhoneError(null);
              setForm(next);
            }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              onClick={() => void handleSave()}
              disabled={!form.name.trim() || !isCustomerPhoneValid(form)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('terminology.deleteConfirm', { entity: terms.singular })}
        message={t('common.deleteMessage', { name: customer?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </EntityDetailShell>
  );
}
